import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Coins, Menu, MessageSquare, LogOut, User, Store, ReceiptText, Package, Wallet, Bell } from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Logo = () => (
  <Link to="/" data-testid="header-logo" className="flex items-center gap-2">
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-700 text-sm font-extrabold text-white">T</span>
    <span className="text-xl font-extrabold tracking-tight text-stone-900">Tuntas</span>
  </Link>
);

const NAV = {
  pelanggan: [
    { to: "/permintaan-saya", label: "Permintaan Saya", id: "nav-my-requests" },
    { to: "/pesanan", label: "Pesanan", id: "nav-orders" },
    { to: "/pesan", label: "Pesan", id: "nav-chats", chat: true },
  ],
  penyedia: [
    { to: "/dasbor/permintaan-masuk", label: "Permintaan Masuk", id: "nav-leads" },
    { to: "/dasbor/penawaran", label: "Penawaran Saya", id: "nav-my-quotes" },
    { to: "/pesanan", label: "Pesanan", id: "nav-orders" },
    { to: "/pesan", label: "Pesan", id: "nav-chats", chat: true },
  ],
};

function useUnread(user) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!user?.role || user.role === "admin") return;
    let alive = true;
    const tick = () => api.get("/chats/unread").then((r) => alive && setCount(r.data.count)).catch(() => {});
    tick();
    const id = setInterval(tick, 5000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [user?.user_id, user?.role]);
  return count;
}

const UnreadDot = ({ count, testId }) =>
  count > 0 ? (
    <span data-testid={testId} className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-teal-700 px-1.5 text-[11px] font-bold text-white">
      {count}
    </span>
  ) : null;

const navCls = ({ isActive }) =>
  `flex items-center rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${isActive ? "bg-teal-50 text-teal-800" : "text-stone-600 hover:text-stone-900"}`;

const UserMenu = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const items = user.role === "penyedia"
    ? [["/dasbor/profil", "Profil Usaha", Store, "menu-provider-profile"], ["/dasbor/paket", "Paket Saya", Package, "menu-packages"],
      ["/dasbor/penghasilan", "Penghasilan", Wallet, "menu-earnings"], ["/dasbor/kredit", "Kredit", ReceiptText, "menu-credits"],
      ["/notifikasi", "Notifikasi", Bell, "menu-notifications"], ["/akun", "Akun Saya", User, "menu-account"]]
    : user.role === "pelanggan" ? [["/notifikasi", "Notifikasi", Bell, "menu-notifications"], ["/akun", "Akun Saya", User, "menu-account"]] : [];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-trigger" className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-stone-200 text-sm font-bold text-stone-700 ring-offset-2 hover:ring-2 hover:ring-teal-600/40">
          {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user.name || "?")[0].toUpperCase()}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-xl">
        <DropdownMenuLabel>
          <p className="truncate text-sm font-semibold">{user.name}</p>
          <p className="truncate text-xs font-normal text-stone-500">{user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map(([to, label, Icon, id]) => (
          <DropdownMenuItem key={to} data-testid={id} onClick={() => navigate(to)}>
            <Icon className="mr-2 h-4 w-4" /> {label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem data-testid="logout-button" onClick={onLogout}>
          <LogOut className="mr-2 h-4 w-4" /> Keluar
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const CreditPill = ({ credits }) => (
  <Link to="/dasbor/kredit" data-testid="header-credit-balance" className="flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-900">
    <Coins className="h-3.5 w-3.5" /> <span data-testid="header-credit-value">{credits}</span> kredit
  </Link>
);

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const unread = useUnread(user);
  const links = (user && NAV[user.role]) || [];
  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLinks = (prefix) =>
    links.map((l) => (
      <NavLink key={l.to} to={l.to} className={navCls} data-testid={`${prefix}${l.id}`}>
        {l.label}
        {l.chat && <UnreadDot count={unread} testId={`${prefix}unread-count`} />}
      </NavLink>
    ));

  return (
    <header className="sticky top-0 z-40 border-b border-stone-200/70 bg-white/80 backdrop-blur-md" data-testid="site-header">
      <div className="container-page flex h-16 items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Logo />
          <nav className="hidden items-center gap-1 md:flex">{navLinks("")}</nav>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {user?.role === "penyedia" && user.provider && <CreditPill credits={user.provider.credits} />}
          {user?.role === "pelanggan" && (
            <Button asChild className="hidden rounded-full bg-teal-700 hover:bg-teal-800 sm:inline-flex" data-testid="header-request-cta">
              <Link to="/minta-penawaran">Minta Penawaran</Link>
            </Button>
          )}
          {user?.role === "admin" && (
            <Button asChild className="rounded-full bg-teal-700 hover:bg-teal-800" data-testid="header-admin-link"><Link to="/admin">Panel Admin</Link></Button>
          )}
          {user === false && (
            <>
              <Link to="/daftar?peran=penyedia" className="hidden text-sm font-medium text-stone-600 hover:text-teal-800 lg:inline" data-testid="header-become-provider">
                Jadi Penyedia Jasa
              </Link>
              <Button asChild variant="ghost" className="rounded-full" data-testid="header-login-button">
                <Link to="/masuk">Masuk</Link>
              </Button>
              <Button asChild className="hidden rounded-full bg-teal-700 hover:bg-teal-800 sm:inline-flex" data-testid="header-register-button">
                <Link to="/daftar">Daftar</Link>
              </Button>
            </>
          )}
          {user && links.length > 0 && <NotificationBell user={user} />}
          {user && user.role && <UserMenu user={user} onLogout={onLogout} />}
          {links.length > 0 && (
            <Sheet>
              <SheetTrigger asChild>
                <button className="relative rounded-full p-2 text-stone-700 md:hidden" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                  {unread > 0 && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-teal-600" />}
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetTitle className="mb-6 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Menu</SheetTitle>
                <nav className="flex flex-col gap-1">{navLinks("mobile-")}</nav>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </header>
  );
};
